import { getDataStatus } from '@r1-frontend/entities/Layout/_deps/helpers/functions';

import { dataLayerPush, TDataLayerPushArgument } from '@r1-frontend/analytics/dataLayerPush';

import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

interface IData {
    action?: string,
    requestId?: number | null,
    isPopup?: boolean,
    option?: string,
    label?: string,
    uid?: number | null,
    category?: string,
}

export interface IDataLayerPushUid extends Omit<TDataLayerPushArgument<unknown>, 'uid'> {
    uid?: number | null,
}

interface IFormat {
    requestId: number | null,
    isPopup?: boolean,
    action: string,
    option?: string,
}

interface IDataLayerForCBPhonePushWrapper {
    status: number,
    message?: string,
    isPopup?: boolean,
    option?: string,
    uid?: number | null,
    label?: string,
}

/**
 * Возвращает свойство label в нужном формате с данными
 *
 * @function
 * @name helpers.getFormattedLabel
 * @param {number | null} requestId - id ответа запроса
 * @param {boolean} isPopup - место вызова
 * @param {string} action - тип действия события
 * @param {string} option - дополнительные свойства события как название акции/партнёрского сервиса/продукта и тд.
 * @returns {string | null}
 */
export const getFormattedLabel = ({ isPopup, requestId, option, action }: IFormat): string => {
    let id: null | string = '';
    let result = '';
    if (action === ACTIONS.finished) {
        id = (typeof requestId === 'number' || null) && String(requestId);
    }

    if (isPopup) {
        result = 'popup';
    }

    if (option) {
        result += `${isPopup ? ' | ' : ''}${option}`;
    }

    return result + `${result && id ? ` | ${id}` : id ? id : ''}`;
};

/**
 * Создает событие для аналитики типа UAevent - finished/error/started
 *
 * @function
 * @name helpers.dataLayerForCBPhonePush
 * @param {string} action - тип действия события
 * @param {number | null} requestId - id ответа запроса
 * @param {boolean} isPopup - место вызова
 * @param {string} option - дополнительные свойства события как название акции/партнёрского сервиса/продукта и тд.
 * @param {string} category - параметр категории
 * @returns void
 */
export const dataLayerForCBPhonePush = ({
    action = '',
    label,
    requestId = null,
    isPopup = false,
    option,
    uid,
    category = CATEGORIES.applicationForm,
}: IData): void => {
    const actionWrap = action || (requestId ? ACTIONS.finished : ACTIONS.error);

    if (uid) {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: category,
            action: actionWrap,
            label: label || getFormattedLabel({ isPopup, requestId, option, action: actionWrap }),
            uid,
        });
    } else {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: category,
            action: actionWrap,
            label: label || getFormattedLabel({ isPopup, requestId, option, action: actionWrap }) || '',
        });
    }
};

export const dataLayerForCBPhonePushWrapper = ({ status, label, isPopup, option, uid }: IDataLayerForCBPhonePushWrapper): void => {
    const { ok } = getDataStatus(status);

    dataLayerForCBPhonePush({
        action: ok ? ACTIONS.finished : ACTIONS.error,
        label,
        isPopup,
        option,
        uid,
    });
};

/**
 * Отправляет событие VAS
 *
 * @function
 * @name helpers.sendVasGA
 * @param {string} action - дополнительный action
 * @param {string} label - label
 * @returns void
 */
export const sendVasGA = (action: string, label?: string): void => {
    dataLayerPush({
        event: EVENTS.UAevent,
        category: CATEGORIES.vas,
        action,
        label,
    });
};

/**
 * Создает функцию для отпрвки события с дополнительным label
 * example: 'partner services:'
 *
 * @function
 * @name helpers.createSendVasGA
 * @param {string} additionalLabel - дополнительный label
 * @returns void
 */
export const createSendVasGA = (additionalLabel: string) => (action: string, label?: string): void => {
    sendVasGA(action, label && [additionalLabel, label].join(' '));
};

/**
 * Разделение пользователей заказа обратного звонка GA
 *
 * @function
 * @name helpers.divideUsersGA
 * @param {string} orderType - статус заявки
 * @param {string} isAuth - авторизованная/неавторизованная зона
 * @returns string
 */
export const divideUsersGA = (orderType: string | boolean, isAuth: boolean): string => {
    const resultForAuth = orderType ? ACTIONS.orderEquip : ACTIONS.errorEquip;
    const resultForNotAuth = orderType ? ACTIONS.finished : ACTIONS.error;
    return isAuth ? resultForAuth : resultForNotAuth;
};
